import React, { createContext, useContext, useState, useEffect } from "react";
import { getToken, getUser, setToken, setUser, removeToken, removeUser, apiRequest } from "../lib/utils";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      // CRITICAL: Se retornando do OAuth callback, pular a verificação /me
      // AuthCallback vai trocar o session_id e estabelecer a sessão primeiro
      if (window.location.hash?.includes('session_id=')) {
        setLoading(false);
        return;
      }
      
      const token = getToken();
      const savedUser = getUser();
      
      if (token && savedUser) {
        try {
          const userData = await apiRequest("/auth/me");
          setUserState(userData);
          setUser(userData);
        } catch (error) {
          console.error("Erro ao verificar autenticação:", error);
          
          // Só faz logout se for erro de autenticação (401)
          // Para outros erros (rede, timeout), mantém o usuário salvo
          if (error.message === "Sessão expirada" || error.message === "Senha incorreta") {
            removeToken();
            removeUser();
          } else {
            // Mantém o usuário salvo para não perder sessão por erro temporário
            console.log("Mantendo sessão salva devido a erro temporário");
            setUserState(savedUser);
          }
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, senha) => {
    const response = await apiRequest("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, senha }),
    });
    
    setToken(response.access_token);
    // Incluir deve_trocar_senha no objeto do usuário
    const userWithFlags = {
      ...response.user,
      deve_trocar_senha: response.deve_trocar_senha || false
    };
    setUser(userWithFlags);
    setUserState(userWithFlags);
    
    return response;
  };

  const register = async (dados) => {
    const response = await apiRequest("/auth/register", {
      method: "POST",
      body: JSON.stringify(dados),
    });
    
    // Se o cadastro está pendente, não faz login automático
    if (response.status === "pendente") {
      return response;
    }
    
    // Comportamento para login automático (trial ou aprovação automática)
    // Aceita tanto 'token' quanto 'access_token' para compatibilidade
    const token = response.token || response.access_token;
    if (token) {
      setToken(token);
      setUser(response.user);
      setUserState(response.user);
    }
    
    return response;
  };

  const logout = () => {
    removeToken();
    removeUser();
    setUserState(null);
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser usado dentro de AuthProvider");
  }
  return context;
};
